import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { products } from '../data/mockData';
import ProductCard from '../components/ProductCard';
import SkeletonCard from '../components/SkeletonCard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFilter, FiSearch } = FiIcons;

export default function Shop({ onAddToCart }) {
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const location = useLocation();

  const families = ['All', 'Woodsy', 'Earthy', 'Sweet', 'Citrus', 'Fruity'];

  useEffect(() => {
    // Simulate loading for the premium library experience
    const timer = setTimeout(() => setIsLoading(false), 800);
    
    const params = new URLSearchParams(location.search);
    const familyParam = params.get('family');
    if (familyParam && families.includes(familyParam)) {
      setFilter(familyParam);
    }
    return () => clearTimeout(timer);
  }, [location]);

  const filteredProducts = products.filter(p => {
    const matchesFilter = filter === 'All' || p.family === filter;
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) || 
                          p.notes.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <main className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <header className="mb-20">
        <h1 className="font-serif text-5xl md:text-7xl text-linen mb-8 tracking-tight">The Library</h1>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 border-b border-white/10 pb-12">
          <p className="text-linen/50 max-w-xl text-lg leading-relaxed">
            Browse our full curation of artisanal fragrances. Each vessel is hand-poured in our studio using sustainable organic wax and rare botanical oils.
          </p>
          <div className="relative w-full md:w-80 group">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-ember transition-colors" />
            <input 
              type="text" 
              placeholder="Search by note or name..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-smoke-light border border-white/5 py-4 pl-12 pr-6 text-sm text-linen outline-none focus:border-ember/50 transition-all rounded-none focus:ring-1 focus:ring-ember/20"
              aria-label="Search fragrances"
            />
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row gap-16">
        <aside className="w-full lg:w-48 flex-shrink-0">
          <div className="sticky top-32">
            <h3 className="flex items-center text-[10px] uppercase tracking-[0.3em] text-ember font-bold mb-8">
              <SafeIcon icon={FiFilter} className="mr-3" />
              Families
            </h3>
            <ul className="space-y-5">
              {families.map(family => (
                <li key={family}>
                  <button 
                    onClick={() => {
                      setIsLoading(true);
                      setFilter(family);
                      setTimeout(() => setIsLoading(false), 400);
                    }}
                    className={`text-sm tracking-widest transition-all duration-300 relative px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ember/30 ${filter === family ? 'text-linen font-bold' : 'text-linen/30 hover:text-linen'}`}
                  >
                    {family}
                    {filter === family && (
                      <motion.div layoutId="activeFilter" className="absolute left-0 top-1/2 -translate-y-1/2 -ml-3 w-1 h-1 bg-ember rounded-full" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="flex-grow">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-20"
              >
                {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
              </motion.div>
            ) : filteredProducts.length > 0 ? (
              <motion.div 
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-20"
              >
                {filteredProducts.map(product => (
                  <div key={product.id} className={product.stock === 0 ? 'opacity-60 grayscale' : ''}>
                    <ProductCard 
                      product={product} 
                      onAddToCart={() => product.stock > 0 && onAddToCart(product)} 
                    />
                    {product.stock === 0 && (
                      <p className="text-[9px] uppercase tracking-[0.2em] text-red-500 font-bold mt-2 ml-1">Currently Out of Stock</p>
                    )}
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div 
                key="empty"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-40 bg-smoke-light border border-white/5"
              >
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-linen/10">
                  <SafeIcon icon={FiSearch} className="w-8 h-8" />
                </div>
                <p className="text-linen/30 font-serif text-2xl italic">"No scent matches your current search."</p>
                <button 
                  onClick={() => {setFilter('All'); setSearch('');}}
                  className="mt-8 text-ember hover:text-linen transition-colors uppercase tracking-[0.2em] text-[10px] font-bold border-b border-ember pb-1"
                >
                  Reset Library
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}